import React from 'react';
import './Header.css';
import { useLocation } from 'react-router-dom';
import { useUserName } from '../../../context/UserNameContext';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import Settings from '@mui/icons-material/Settings';
import Button from '@mui/material/Button';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { useTheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import { darkModeAtom } from '../../../state/theme';

type HeaderProps = {
  signOut?: (data?: any) => void;
};

const HELP_FILE_PATH =
  'https://nissangroup.sharepoint.com/:p:/t/JAO_NML_06_042452_Data_Innovation_Core_member/EXs6ltWsdJ5EnfDGHhLU0CUBkuDW8BBkLW8usyyDpblvUw?e=GcljVM';
const REQUEST_LIST_FILE_PATH =
  'https://nissangroup.sharepoint.com/:x:/t/JAO_NML_06_015280_XQ4_NVCDB_test/IQB9hnX9va5lRIO1zWdCjfihAZtntm77I19EDekKF8HINvQ?e=nwH1m3';

const Header: React.FC<HeaderProps> = ({ signOut }) => {
  // 現在のURLパスを取得
  const location = useLocation();

  // パスに応じてヘッダーのタイトルを決定
  let headerTitle = ''

    // 末尾スラッシュを吸収（/weekly-report/ 対策）
  const normalizedPathname = String(location.pathname ?? '').replace(/\/+$/, '');

  // basename や子ルートでも判定できるように「パスセグメント一致」で見る
  const hasPathSegment = (segment: string): boolean => {
    const re = new RegExp(`(^|/)${segment}(/|$)`);
    return re.test(normalizedPathname);
  };

  if (hasPathSegment('weekly-report')) {
    headerTitle = '週報連';
  } else if (location.pathname === '/master-manage') {
    headerTitle = 'マスタ管理';
  } else if (location.pathname === '/classification-market') {
    headerTitle = '分類市場';
  } else if (hasPathSegment('enlight-sync-check')) {
    headerTitle = 'Enlight連携確認';
  } else if (location.pathname === '/') {
    headerTitle = '入力/修正';
  }

  // ユーザー名（UserProfileの姓+名。未設定時はloginId）をコンテキストから取得
  const userName = useUserName();
  const appVersion = process.env.REACT_APP_APPLICATION_VERSION;

  const [isDark, setIsDark] = useAtom(darkModeAtom);
  const theme = useTheme();

  React.useEffect(() => {
    document.title = '週報アプリ';
  }, []);

  return (
    <>
      <header className="header-content">
        <div className="header" style={{ backgroundColor: theme.palette.mode === 'dark' ? '#0b1120' : theme.palette.background.paper }}>
          <div
            className="header-container"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h1 className="headerTitle">{headerTitle}</h1>
            </div>
            <div className="header-actions">
              <a
                href={HELP_FILE_PATH}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textDecoration: 'none',
                  color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#1976d2',
                  fontSize: '12px',
                  lineHeight: 1,
                  gap: '0.25em',
                }}
                title="使い方ガイド"
              >
                <MenuBookIcon fontSize="medium" />
                <span>使い方ガイド</span>
              </a>

              <a
                href={REQUEST_LIST_FILE_PATH}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textDecoration: 'none',
                  color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#1976d2',
                  fontSize: '12px',
                  lineHeight: 1,
                  gap: '0.25em',
                }}
                title="機能改善要望リスト"
              >
                <FactCheckIcon fontSize="medium" />
                <span>機能改善要望リスト</span>
              </a>

              <div className="header-theme-control" role="group" aria-label="画面の明るさ">
                <span className="header-theme-control-label">画面の明るさ</span>
                <button
                  onClick={() => setIsDark(false)}
                  className={`header-theme-option${!isDark ? ' is-active' : ''}`}
                  type="button"
                  aria-pressed={!isDark}
                  aria-label="明るい画面に切り替える"
                  title="明るい画面に切り替える"
                >
                  <LightModeOutlinedIcon fontSize="small" aria-hidden="true" />
                  <span>ライト</span>
                </button>
                <button
                  onClick={() => setIsDark(true)}
                  className={`header-theme-option${isDark ? ' is-active' : ''}`}
                  type="button"
                  aria-pressed={isDark}
                  aria-label="暗い画面に切り替える"
                  title="暗い画面に切り替える"
                >
                  <DarkModeOutlinedIcon fontSize="small" aria-hidden="true" />
                  <span>ダーク</span>
                </button>
              </div>

              <button
                onClick={(e) => {
                  e.preventDefault();
                  window.dispatchEvent(
                    new CustomEvent('open-user-info-modal', {
                      detail: { editMode: true },
                    })
                  );
                }}
                aria-label={`${userName}さんのユーザー設定を開く`}
                title="ユーザー設定を開く"
                aria-haspopup="dialog"
                className="header-user-button"
              >
                <span style={{ fontWeight: 'bold' }}>{userName}</span>
                <Settings fontSize="small" />
              </button>

              <Button
                variant="outlined"
                size="small"
                onClick={signOut}
                aria-label="ログアウト"
                title="週報アプリからログアウトする"
              >
                ログアウト
              </Button>
            </div>
          </div>
        </div>
      </header>

      {appVersion && (
        <span
          className="env-info"
          title="Information"
          role="note"
          style={{
            position: 'fixed',
            bottom: 4,
            left: 4,
            zIndex: 1,
            fontSize: '12px',
            color: '#555',
            backgroundColor: '#eef5ff',
            border: '1px solid #c9ddff',
            borderRadius: '4px',
            padding: '2px 6px',
            lineHeight: 1.4,
          }}
        >
          Version: {appVersion}
        </span>
      )}
    </>
  );
};

export default Header;
